import Stack from '@mui/material/Stack';
import React from 'react';

import { HomeActivityHeader } from './HomeActivityHeader';
import { HomeMatrixToolbar } from './HomeMatrixToolbar';
import { HomeSkeleton } from './HomeSkeleton';
import { useActivityMatrixView } from './useActivityMatrixView';
import { useActivityMonitor } from './useActivityMonitor';
import { PageContainer } from '../../components/atoms/PageContainer';
import { Panel } from '../../components/atoms/Panel';
import { ServiceActivityMatrix } from '../../components/organisms/ServiceActivityMatrix';
import { ServiceFlowRail } from '../../components/organisms/ServiceFlowRail';
import { PLATFORM_NAME } from '../../configs/common';
import { TabHandler } from '../../handlers/TabHandler';

/**
 * Home — the service activity dashboard.
 *
 * Three bands: a control-room header, the flow rail (the ten statuses shown as
 * the three lanes they actually form), and the activity matrix (client × status
 * counts, grouped and heat-encoded). Four levels of the page drill through to
 * a filtered job list: a rail stage, a matrix cell, a row, or a client group.
 */
export const Home: React.FC = () => {
  TabHandler(PLATFORM_NAME);

  const monitor = useActivityMonitor();
  const matrixView = useActivityMatrixView(monitor.matrix);

  if (monitor.isLoading) return <HomeSkeleton />;

  return (
    // The header sits outside PageContainer so it spans the full width with no
    // radius, matching the Jobs list (ServiceListHeader + PageContainer).
    <>
      <HomeActivityHeader
        refreshEnabled={monitor.refreshEnabled}
        onRefreshToggle={monitor.handleRefreshToggle}
        lastUpdatedTime={monitor.lastUpdatedTime}
        grandTotal={monitor.matrix.grandTotal}
        isFetching={monitor.isFetching}
      />

      <PageContainer>
        <Stack gap={1}>
          <ServiceFlowRail
            lanes={monitor.lanes}
            canDrillDown={monitor.canDrillDown}
            onStageSelect={monitor.handleStageSelect}
          />

          <Panel
            title="Activity matrix"
            bodyPadding="0"
            actions={
              <HomeMatrixToolbar
                search={matrixView.search}
                onSearchChange={matrixView.setSearch}
                rowCount={matrixView.view.rowCount}
                allCollapsed={matrixView.allCollapsed}
                onToggleAll={matrixView.handleToggleAll}
              />
            }
          >
            <ServiceActivityMatrix
              model={matrixView.view}
              sort={matrixView.sort}
              onSort={matrixView.handleSort}
              collapsedGroups={matrixView.collapsedGroups}
              onToggleGroup={matrixView.handleToggleGroup}
              canDrillDown={monitor.canDrillDown}
              onCellSelect={monitor.handleCellSelect}
              onRowSelect={monitor.handleRowSelect}
              onColumnSelect={monitor.handleStageSelect}
              onGroupSelect={monitor.handleGroupSelect}
              emptyMessage={
                monitor.isError
                  ? 'Service activity could not be loaded. It will retry on the next refresh.'
                  : matrixView.isFilteredEmpty
                    ? `No clients or job types match “${matrixView.appliedSearch}”.`
                    : 'No open service activity right now.'
              }
            />
          </Panel>
        </Stack>
      </PageContainer>
    </>
  );
};
